import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Sparkles, Coins, ArrowRight } from 'lucide-react';
import { useSession } from '../context/SessionContext';
import { trackEvent, trackPageView, trackPaymentCompleted } from '../services/analytics';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshSession } = useSession();
  const [synced, setSynced] = useState(false);

  const status = searchParams.get('status');
  const type = searchParams.get('type');
  const packageId = searchParams.get('package');
  const credits = searchParams.get('credits');
  const unlocks = searchParams.get('unlocks');
  const reason = searchParams.get('reason');

  const isSuccess = status === 'success';
  const isCredits = type === 'credits';

  const planNames = {
    starter: 'Starter',
    premium: 'Premium',
    ultimate: 'Ultimate',
    credits_500: '500 Sage Coins',
    credits_1000: '1000 Sage Coins',
    credits_1500: '1500 Sage Coins',
  };

  // Analytics tracking on page load
  useEffect(() => {
    trackPageView('payment_success');
    if (isSuccess) {
      const planName = planNames[packageId] || packageId || 'unknown';
      const amount = packageId === 'starter' ? 199 : packageId === 'premium' ? 599 : packageId === 'ultimate' ? 999 : 0;
      trackPaymentCompleted(planName, amount);
      trackEvent('payment_conversion', { 
        plan: planName, 
        type: isCredits ? 'credits' : 'subscription',
        credits: credits || 0,
        package_id: packageId
      });
    } else {
      trackEvent('payment_failed', { 
        reason: reason || 'unknown', 
        package_id: packageId 
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSuccess && !synced) {
      setSynced(true);
      if (!isCredits) {
        localStorage.setItem('is_premium', 'true');
        localStorage.setItem('isPremium', 'true');
        localStorage.setItem('plan_id', packageId || '');
        if (unlocks) {
          localStorage.setItem('plan_unlocks', JSON.stringify(unlocks.split(',')));
        }
        if (credits) {
          localStorage.setItem('sage_credits', credits);
        }
        const expiryDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
        localStorage.setItem('premium_expires', expiryDate.toISOString());
      } else {
        const current = parseInt(localStorage.getItem('sage_credits') || '0');
        localStorage.setItem('sage_credits', String(current + parseInt(credits || '0')));
      }
      window.dispatchEvent(new Event('credits-updated'));
      if (refreshSession) refreshSession();
    }
  }, [isSuccess, synced, isCredits, packageId, unlocks, credits, refreshSession]);

  return (
    <div
      data-testid="payment-success-page"
      style={{
        height: '100vh',
        background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', duration: 0.6 }}
        style={{ maxWidth: 380, width: '100%', textAlign: 'center' }}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
          style={{
            width: 80, height: 80, margin: '0 auto 24px', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: isSuccess ? 'rgba(52, 211, 153, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            border: `2px solid ${isSuccess ? '#34D399' : '#EF4444'}`,
          }}
        >
          {isSuccess ? <Check size={40} color="#34D399" /> : <span style={{ fontSize: 36, color: '#EF4444' }}>!</span>}
        </motion.div>

        <motion.h1
          initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3 }}
          style={{ fontSize: 24, fontWeight: 700, color: isSuccess ? '#34D399' : '#EF4444', marginBottom: 8 }}
        >
          {isSuccess ? 'Payment Successful!' : 'Payment Failed'}
        </motion.h1>

        <motion.p
          initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.4 }}
          style={{ fontSize: 14, color: 'rgba(226,232,240,0.6)', marginBottom: 28, lineHeight: 1.6 }}
        >
          {isSuccess ? (
            isCredits
              ? <>+{credits || 0} Sage Coins added to your account</>
              : <>{planNames[packageId] || 'Plan'} activated successfully</>
          ) : (
            <>Something went wrong. {reason === 'signature_failed' ? 'Payment verification failed.' : 'Please try again.'}</>
          )}
        </motion.p>

        {isSuccess && (
          <motion.div
            initial={{ y: 15, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}
            style={{
              background: 'rgba(116,179,255,0.06)', border: '1px solid rgba(116,179,255,0.15)',
              borderRadius: 16, padding: 20, marginBottom: 28,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 12 }}>
              {isCredits ? <Coins size={18} color="#74B3FF" /> : <Sparkles size={18} color="#74B3FF" />}
              <span style={{ fontSize: 16, fontWeight: 600, color: '#E2E8F0' }}>
                {planNames[packageId] || packageId}
              </span>
            </div>
            {credits && !isCredits && (
              <p style={{ fontSize: 12, color: 'rgba(226,232,240,0.4)' }}>+{credits} Sage Coins included</p>
            )}
            {unlocks && (
              <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                {unlocks.split(',').map((u) => (
                  <span key={u} style={{
                    background: 'rgba(116,179,255,0.1)', border: '1px solid rgba(116,179,255,0.2)',
                    borderRadius: 8, padding: '4px 10px', fontSize: 11, color: '#74B3FF',
                  }}>{u.replace(/_/g, ' ')}</span>
                ))}
              </div>
            )}
          </motion.div>
        )}

        <motion.button
          initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6 }}
          whileTap={{ scale: 0.96 }}
          data-testid="payment-success-continue-btn"
          onClick={() => navigate(isSuccess ? '/dashboard' : '/payment-plans')}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            width: '100%', padding: '14px 24px',
            background: isSuccess ? 'linear-gradient(135deg, #34D399 0%, #059669 100%)' : 'linear-gradient(135deg, #74B3FF 0%, #5F42C9 100%)',
            border: 'none', borderRadius: 14, color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer',
          }}
        >
          {isSuccess ? 'Continue to Dashboard' : 'Try Again'}
          <ArrowRight size={16} />
        </motion.button>
      </motion.div>
    </div>
  );
};

export default PaymentSuccess;
